import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { QuotationPage as QuotationPageType, QuotationBlock, FlightInfo, FlightConnection, AccommodationInfo, TourInfo, TransportationInfo } from './types';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import { useToast } from '@/hooks/use-toast';
import { useAmadeusApi } from '@/hooks/useAmadeusApi';
import { useOpenAiApi } from '@/hooks/useOpenAiApi';
import { 
  ChevronDown, 
  ChevronRight, 
  Plus, 
  Trash, 
  Calendar, 
  ImagePlus, 
  Plane,
  Building,
  Map,
  Search,
  Loader2,
  Info,
  DollarSign,
  Wand2,
  MapPin,
  InfoIcon,
  X,
  CircleDot,
  Tag,
  AlignLeft,
  Clock,
  Image,
  FileText,
  Settings,
  Users,
  ClipboardList,
  Car
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Droppable, DragDropContext } from '@hello-pangea/dnd';
import { QuotationBlock as QuotationBlockComponent } from './QuotationBlock';
import { Label } from '@/components/ui/label';
import { formatSafeDate } from '@/lib/date-utils';
import { ImageManagerModal } from '@/components/ui/image-manager-modal';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { PlacesAutocompleteV1 } from '@/components/ui/places-autocomplete-v1';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { 
  format, 
  differenceInMinutes, 
  isValid, 
  parse, 
  parseISO as dateFnsParseISO,
  addDays
} from 'date-fns';
import { FlightCollapsible } from './FlightCollapsible';
import { AccommodationCollapsible } from './AccommodationCollapsible';
import { TourCollapsible } from './TourCollapsible';
import { debugBeforeSave, monitorStoredTourCoordinates, forceUpdateTourCoordinates } from "@/lib/quotation-storage";
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { QuotationPageTabs } from './QuotationPageTabs';

interface QuotationPageProps {
  page: QuotationPageType;
  index: number;
  updatePage: (id: string, field: keyof QuotationPageType, value: any) => void;
  deletePage: (id: string) => void;
  addBlock: (pageId: string) => void;
  updateBlock: (pageId: string, blockId: string, field: keyof QuotationBlock, value: any) => void;
  deleteBlock: (pageId: string, blockId: string) => void;
  duplicateBlock?: (pageId: string, blockId: string) => void;
  moveTourToPage?: (sourcePageId: string, tourId: string, targetPageId: string) => void;
  moveRestaurantToPage?: (sourcePageId: string, restaurantId: string, targetPageId: string) => void;
  moveAccommodationToPage?: (sourcePageId: string, accommodationId: string, targetPageId: string) => void;
  moveFlightEventToPage?: (sourcePageId: string, flightEventId: string, targetPageId: string) => void;
  moveAccommodationEventToPage?: (sourcePageId: string, accommodationEventId: string, targetPageId: string) => void;
  moveTransportationToPage?: (sourcePageId: string, transportationId: string, targetPageId: string) => void;
  moveFlightToPage?: (sourcePageId: string, flightId: string, targetPageId: string) => void;
  moveBlockToPage?: (sourcePageId: string, blockId: string, targetPageId: string) => void;
  allPages?: QuotationPageType[]; // All pages in the quotation
}

// Custom styled label with orange underline and icon
const StyledLabel = ({ 
  children, 
  htmlFor, 
  className, 
  icon: Icon = CircleDot,
  ...props 
}: React.ComponentProps<typeof Label> & { icon?: React.ElementType }) => (
  <Label 
    htmlFor={htmlFor}
    className={cn("border-b border-voah-coral pb-1 inline-flex items-center gap-1.5 text-left w-fit text-sm font-medium text-gray-700", className)}
    {...props}
  >
    <Icon className="h-3.5 w-3.5 text-voah-coral flex-shrink-0" />
    <span className="whitespace-nowrap">{children}</span>
  </Label>
);

// Update input styles by creating a component wrapper
const FormInput = ({ className, ...props }: React.ComponentProps<typeof Input>) => (
  <Input
    className={cn("text-sm text-gray-600 font-normal", className)}
    {...props}
  />
);

// Função para formatar data de forma segura com formato específico
const formatSafeDateWithFormat = (date: Date | string | null | undefined, dateFormat: string = 'yyyy-MM-dd') => {
  if (!date) return '';

  try {
    // Se for string, converter para Date
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    
    // Verificar se é uma data válida
    if (!dateObj || !(dateObj instanceof Date) || isNaN(dateObj.getTime())) {
      return '';
    }
    
    return format(dateObj, dateFormat);
  } catch (error) {
    console.error("Erro ao formatar data:", error);
    return '';
  }
};

// Funções auxiliares para voos
const getTimeFromISOString = (isoString: string | undefined): string => {
  if (!isoString || !isoString.includes('T')) return '00:00';
  return isoString.split('T')[1].substring(0, 5); // Returns HH:mm
};

const getDateFromISOString = (isoString: string | undefined): string => {
  if (!isoString || !isoString.includes('T')) return format(new Date(), 'yyyy-MM-dd');
  return isoString.split('T')[0]; // Returns YYYY-MM-DD
};

// Update the calculateMinutesBetweenISOStrings function to never return negative values
const calculateMinutesBetweenISOStrings = (start: string, end: string): number => {
  if (!start || !end) return 0;
  
  try {
    const startDate = new Date(start);
    const endDate = new Date(end);
    
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      return 0;
    }
    
    // Calculate the difference and ensure it's never negative
    const diffInMinutes = differenceInMinutes(endDate, startDate);
    return Math.max(0, diffInMinutes); // Return 0 if the difference is negative
  } catch (error) {
    console.error("Erro ao calcular duração:", error);
    return 0;
  }
};

// Update the formatDuration function to use HH:MM format
const formatDuration = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  
  // Format as HH:MM
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
};

// Helper function to parse duration string like "2h 30m" or "02:30" into minutes
const parseDurationToMinutes = (duration: string): number => {
  if (!duration) return 0;
  
  try {
    // Handle "HH:MM" format
    if (duration.includes(':')) {
      const [hours, minutes] = duration.split(':').map(part => parseInt(part, 10));
      return (hours || 0) * 60 + (minutes || 0);
    }
    
    // Handle "Xh Ym" format for backward compatibility
    const hoursMatch = duration.match(/(\d+)\s*h/);
    const minutesMatch = duration.match(/(\d+)\s*m/);
    
    const hours = hoursMatch ? parseInt(hoursMatch[1], 10) : 0;
    const minutes = minutesMatch ? parseInt(minutesMatch[1], 10) : 0;
    
    return hours * 60 + minutes;
  } catch (error) {
    console.error("Error parsing duration:", error);
    return 0;
  }
};

const parseISO = (dateString: string | undefined): Date | null => {
  if (!dateString) return null;
  
  try {
    // Assegurar que a string da data está no formato ISO
    if (!dateString.includes('T')) {
      dateString = `${dateString}T00:00:00`;
    }
    
    const parsedDate = parse(dateString, "yyyy-MM-dd'T'HH:mm:ss", new Date());
    return isValid(parsedDate) ? parsedDate : null;
  } catch (error) {
    console.error("Erro ao parsear data ISO:", error, dateString);
    return null;
  }
};

// Verifica se o número do voo segue o padrão correto (2 letras seguidas de 1-4 números)
const validateFlightNumber = (number: string): boolean => {
  if (!number) return false;
  
  // Remover espaços e converter para maiúsculas
  const formattedNumber = formatFlightNumber(number);
  
  // Verificar o padrão: 2 letras seguidas de 1-4 números
  const regex = /^[A-Z]{2}\d{1,4}$/;
  return regex.test(formattedNumber);
};

// Formata o número do voo para o padrão esperado pela API (2 letras maiúsculas seguidas de números)
const formatFlightNumber = (number: string): string => {
  if (!number) return '';
  
  // Remover espaços e converter para maiúsculas
  let formatted = number.replace(/\s+/g, '').toUpperCase();
  
  // Se o formato é algo como "LA 1234" ou "la1234", converter para "LA1234"
  const match = formatted.match(/^([A-Z]{2})(\d{1,4})$/);
  if (match) {
    return `${match[1]}${match[2]}`;
  }
  
  return formatted;
};

// Mapeamento básico de códigos de companhias aéreas para nomes
const getAirlineName = (carrierCode: string) => {
  const airlines: Record<string, string> = {
    'LA': 'LATAM Airlines',
    'G3': 'GOL Linhas Aéreas',
    'AD': 'Azul Linhas Aéreas',
    'AA': 'American Airlines',
    'DL': 'Delta Air Lines',
    'UA': 'United Airlines',
    'BA': 'British Airways',
    'LH': 'Lufthansa',
    'AF': 'Air France',
    'KL': 'KLM',
    'IB': 'Iberia',
    'AZ': 'Alitalia',
    'EK': 'Emirates',
    'QR': 'Qatar Airways',
    'EY': 'Etihad Airways',
    'TK': 'Turkish Airlines',
    'SQ': 'Singapore Airlines',
    'CX': 'Cathay Pacific',
    'JL': 'Japan Airlines',
    'NH': 'ANA All Nippon Airways',
  };
  
  return airlines[carrierCode] || carrierCode;
};

export function QuotationPageComponent({
  page,
  index,
  updatePage,
  deletePage,
  addBlock,
  updateBlock,
  deleteBlock,
  duplicateBlock,
  moveTourToPage,
  moveRestaurantToPage,
  moveAccommodationToPage,
  moveFlightEventToPage,
  moveAccommodationEventToPage,
  moveTransportationToPage,
  moveFlightToPage,
  moveBlockToPage,
  allPages = []
}: QuotationPageProps) {
  const { toast } = useToast();
  const { chatCompletion } = useOpenAiApi();
  
  const [isCollapsed, setIsCollapsed] = useState(page.isCollapsed || false);
  const [isImageManagerOpen, setIsImageManagerOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [collapsedBlocks, setCollapsedBlocks] = useState<Record<string, boolean>>({});
  const [isProcessingPageDescription, setIsProcessingPageDescription] = useState(false);
  
  // Inicializa os estados de colapso para todos os itens existentes e escuta eventos
  useEffect(() => {
    // Inicializar os estados de colapso para todos os blocos existentes
    const initBlocksCollapsed = page.blocks?.reduce((acc, block) => {
      acc[block.id] = true;
      return acc;
    }, {} as Record<string, boolean>) || {};
    
    setCollapsedBlocks(initBlocksCollapsed);
  }, [page.blocks]);

  // Função para colapsar/expandir a página inteira
  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
    updatePage(page.id, 'isCollapsed', !isCollapsed);
  };

  // Função para atualizar as imagens da página
  const handleUpdateImages = (images: string[]) => {
    updatePage(page.id, 'images', images);
  };
  
  // Função para controlar a visibilidade das seções da página
  const toggleFeatureVisibility = (feature: 'showFlights' | 'showAccommodations' | 'showTours' | 'showTransportations' | 'showRestaurants' | 'showMap') => {
    updatePage(page.id, feature, !page[feature]);
  };

  // Função para controlar o colapso dos blocos
  const toggleBlockCollapse = (blockId: string) => {
    // Se o bloco atual está colapsado (fechado), vamos abri-lo e fechar todos os outros
    if (collapsedBlocks[blockId]) {
      // Fechar todos os itens primeiro
      const allCollapsed = Object.keys(collapsedBlocks).reduce((acc, id) => {
        acc[id] = true;
        return acc;
      }, {} as Record<string, boolean>);
      
      // Abrir apenas o bloco atual
      setCollapsedBlocks({
        ...allCollapsed,
        [blockId]: false
      });
    } else {
      // Se está aberto, apenas fechamos
      setCollapsedBlocks(prev => ({
      ...prev,
        [blockId]: true
      }));
    }
  };

  // Função para processar a descrição da página com IA
  const handleProcessPageDescription = async () => {
    if (!page.description || page.description.trim() === '') {
      toast({
        title: "Texto vazio",
        description: "Por favor, insira um texto para ser processado pela IA.",
        variant: "destructive"
      });
      return;
    }

    setIsProcessingPageDescription(true);
    
    try {
      // Extract text from HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = page.description;
      const textContent = tempDiv.textContent || tempDiv.innerText || '';

      if (!textContent.trim()) {
        toast({
          title: "Texto vazio",
          description: "Por favor, insira um texto para ser processado pela IA.",
          variant: "destructive"
        });
        setIsProcessingPageDescription(false);
        return;
      }

      toast({
        title: "Processando texto",
        description: "Enviando seu texto para processamento com IA...",
      });
      
      // Enviar apenas o texto do usuário, sem contexto ou instruções adicionais
      // Increase maxTokens to avoid truncation
      const response = await chatCompletion(textContent, 'gpt-4-turbo', 0.7, 1500);
      
      // Extrair a resposta do ChatGPT
      const generatedContent = response.choices[0].message.content.trim();
      
      // Check if the response was truncated
      if (response.choices[0].finish_reason === 'length') {
        toast({
          title: "Aviso de truncamento",
          description: "O texto gerado foi truncado devido ao limite de tamanho. Considere dividir o conteúdo em partes menores.",
          variant: "default"
        });
      }
      
      // Atualizar o campo de descrição
      updatePage(page.id, 'description', generatedContent);
      
      toast({
        title: "Texto gerado",
        description: "O texto foi processado com IA e atualizado com sucesso.",
      });
    } catch (error) {
      console.error('Erro ao processar texto com IA:', error);
      
      toast({
        title: "Erro no processamento",
        description: error instanceof Error ? error.message : "Ocorreu um erro ao processar o texto com IA.",
        variant: "destructive"
      });
    } finally {
      setIsProcessingPageDescription(false);
    }
  };

  return (
    <Card className={cn("shadow-sm rounded-lg overflow-hidden", isCollapsed && "border-l-4 border-l-[#087a83]")}>
      {/* Detalhes da Página (Collapsible) */}
      <CardHeader className="p-4">
        <div 
          className="flex items-center justify-between cursor-pointer"
          onClick={() => setDetailsOpen(!detailsOpen)}
        >
          <div className="flex items-center">
            <Info className="h-5 w-5 text-gray-500 mr-2" />
            <h3 className="text-base font-medium">Detalhes da Página</h3>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="p-0 h-8 w-8"
          >
            {detailsOpen ? (
              <ChevronDown className="h-5 w-5 text-gray-500" />
            ) : (
              <ChevronRight className="h-5 w-5 text-gray-500" />
            )}
          </Button>
        </div>
      </CardHeader>
      
      {detailsOpen && (
        <CardContent className="p-4 pt-0 space-y-4">
          <div className="space-y-2 flex flex-col">
            <StyledLabel icon={Tag}>Título da Página</StyledLabel>
            <FormInput
              value={page.title || ''}
              onChange={(e) => updatePage(page.id, 'title', e.target.value)}
              placeholder="Título da página"
            />
          </div>

          <div className="space-y-2 flex flex-col">
            <StyledLabel icon={AlignLeft}>Descrição</StyledLabel>
            <RichTextEditor
              value={page.description || ''}
              onChange={(value) => updatePage(page.id, 'description', value)}
              placeholder="Descrição desta página..."
              rows={5}
              onProcessWithAI={handleProcessPageDescription}
              isProcessing={isProcessingPageDescription}
              className="bg-white"
            />
          </div>

          <div className="space-y-2 flex flex-col">
            <div className="flex items-center justify-between">
              <StyledLabel icon={Image}>Imagens</StyledLabel>
              <Button 
                type="button" 
                variant="outline" 
                size="sm"
                onClick={() => setIsImageManagerOpen(true)}
                className="h-8 bg-white"
              >
                <ImagePlus className="h-4 w-4 mr-2" />
                <span>Gerenciar imagens</span>
              </Button>
            </div>
            
            {page.images && page.images.length > 0 && (
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2 mt-2">
                {page.images.map((image, idx) => (
                  <div key={idx} className="relative group aspect-square">
                    <img 
                      src={image} 
                      alt={`Imagem ${idx + 1}`} 
                      className="w-full h-full object-cover rounded-md"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="pt-4 border-t">
            <h3 className="text-base font-medium mb-3">Configurações da Página</h3>
            <div className="space-y-2 mb-4">
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id={`show-flights-${page.id}`} 
                  checked={page.showFlights} 
                  onCheckedChange={() => toggleFeatureVisibility('showFlights')}
                />
                <Label 
                  htmlFor={`show-flights-${page.id}`}
                  className="cursor-pointer text-sm font-normal text-gray-600"
                >
                  Exibir seção de Voos
                </Label>
              </div>
              
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id={`show-accommodations-${page.id}`} 
                  checked={page.showAccommodations} 
                  onCheckedChange={() => toggleFeatureVisibility('showAccommodations')}
                />
                <Label 
                  htmlFor={`show-accommodations-${page.id}`}
                  className="cursor-pointer text-sm font-normal text-gray-600"
                >
                  Exibir seção de Hospedagens
                </Label>
              </div>
              
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id={`show-tours-${page.id}`} 
                  checked={page.showTours} 
                  onCheckedChange={() => toggleFeatureVisibility('showTours')}
                />
                <Label 
                  htmlFor={`show-tours-${page.id}`}
                  className="cursor-pointer text-sm font-normal text-gray-600"
                >
                  Exibir seção de Passeios
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox 
                  id={`show-transportations-${page.id}`} 
                  checked={page.showTransportations} 
                  onCheckedChange={() => toggleFeatureVisibility('showTransportations')}
                />
                <Label 
                  htmlFor={`show-transportations-${page.id}`}
                  className="cursor-pointer text-sm font-normal text-gray-600"
                >
                  Exibir seção de Traslados
                </Label>
              </div>
              
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id={`show-restaurants-${page.id}`} 
                  checked={page.showRestaurants} 
                  onCheckedChange={() => toggleFeatureVisibility('showRestaurants')}
                />
                <Label 
                  htmlFor={`show-restaurants-${page.id}`}
                  className="cursor-pointer text-sm font-normal text-gray-600"
                >
                  Exibir seção de Comes e Bebes
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox 
                  id={`show-map-${page.id}`} 
                  checked={page.showMap !== false} 
                  onCheckedChange={(checked) => updatePage(page.id, 'showMap', checked)}
                />
                <Label 
                  htmlFor={`show-map-${page.id}`}
                  className="cursor-pointer text-sm font-normal text-gray-600"
                >
                  Exibir mapa introdutório
                </Label>
              </div>

              <div className="pt-3 border-t mt-3">
                <h4 className="text-sm font-medium mb-2 text-gray-700">Navegação</h4>
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id={`exclude-from-dropdown-${page.id}`} 
                    checked={!!page.excludeFromDropdown}
                    onCheckedChange={(checked) => updatePage(page.id, 'excludeFromDropdown', checked)}
                  />
                  <Label 
                    htmlFor={`exclude-from-dropdown-${page.id}`}
                    className="cursor-pointer text-sm font-normal text-gray-600"
                  >
                    Sempre exibir como tab individual (nunca agrupar no dropdown)
                  </Label>
                </div>
                <div className="text-xs text-gray-500 mt-1 ml-6">
                  Quando ativado, esta página será exibida como uma tab individual mesmo quando o modo dropdown de páginas estiver ativo
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      )}

      {/* Componente de Visualização em Tabs */}
      {!isCollapsed && (
        <CardContent className="p-0">
          <QuotationPageTabs 
            page={page}
            updatePage={updatePage}
            updateBlock={updateBlock}
            deleteBlock={deleteBlock}
            duplicateBlock={duplicateBlock}
            addBlock={addBlock}
            moveTourToPage={moveTourToPage}
            moveRestaurantToPage={moveRestaurantToPage}
            moveAccommodationToPage={moveAccommodationToPage}
            moveFlightEventToPage={moveFlightEventToPage}
            moveAccommodationEventToPage={moveAccommodationEventToPage}
            moveTransportationToPage={moveTransportationToPage}
            moveFlightToPage={moveFlightToPage}
            moveBlockToPage={moveBlockToPage}
            allPages={allPages}
          />
        </CardContent>
      )}

      {/* Image Manager Modal */}
      {isImageManagerOpen && (
        <ImageManagerModal
          isOpen={isImageManagerOpen}
          onClose={() => setIsImageManagerOpen(false)}
          images={page.images || []}
          onImagesChange={handleUpdateImages}
          contextId={page.id}
          contextType="customSection"
        />
      )}
    </Card>
  );
} 